// Components==============
import updateUser from 'actions/user/update';
import Button from 'components/UI/Button/Button';
import Dropdown from 'components/UI/Dropdown/Dropdown';
import FormDropdown from 'components/UI/Form/FormDropdown';
import Input from 'components/UI/Form/Input';
import Modal from 'components/UI/Modal/Modal';
import Spinner from 'components/UI/Spinner/Spinner';
import { useFormValues } from 'hooks/useFormValues';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import React, { useEffect, useState } from 'react';
import FormContext from 'utilities/FormContext';
import './Settings.module.scss';
import ChangeAvatar from './components/ChangeAvatar/ChangeAvatar';
// =========================

export default function Settings() {
  const user = useRedux((state) => state.authentication?.user);

  const isExtern = usePermission(['Extern']);

  const {
    formValues,
    handleChange,
    errors,
    setErrors,
    setFormValues,
  } = useFormValues({
    id: user?.id,
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirmation: '',
    language: '',
    phonenumber: '',
    website: '',
    company: '',
  });

  useEffect(() => {
    const first_name = user?.first_name;
    const last_name = user?.last_name;
    const email = user?.email;
    const language = user?.language;
    const phonenumber = user?.phonenumber || '';
    const company = user?.company || '';
    const website = user?.website || '';

    if (first_name && last_name && email && language)
      setFormValues((prev) => ({
        ...prev,
        first_name,
        last_name,
        email,
        language,
        phonenumber,
        company,
        website,
      }));
  }, []);

  const translate = useTranslate();
  const dispatch = useReduxAction();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setIsSubmitting(true);

    const formValuesCopy = formValues;

    if (!formValues.password || !formValues.password_confirmation) {
      // @ts-ignore
      delete formValuesCopy.password;
      // @ts-ignore
      delete formValuesCopy.password_confirmation;
    }

    dispatch(updateUser(formValuesCopy))
      .then(() => setIsSubmitting(false)) // updateUser's return triggers an error?
      .catch(({ response }: { response: any }) => {
        setIsSubmitting(false);
        setErrors((prev: any) => ({ ...prev, ...response?.data?.errors }));
      })
      .then(() => {
        window.location.reload(false);
      });
  }

  return (
    <Modal size="medium">
      <div styleName="wrapper">
        <ChangeAvatar />
        <form onSubmit={handleSubmit}>
          <FormContext
            onChange={handleChange}
            errors={errors}
            formValues={formValues}
            setFormValues={setFormValues}
          >
            <Input
              color="outline-grey"
              name="first_name"
              label={translate('general.terms.first_name')}
              required={false}
            />
            <Input
              color="outline-grey"
              name="last_name"
              label={translate('general.terms.last_name')}
              required={false}
            />
            <Input
              color="outline-grey"
              name="email"
              label={translate('general.terms.email')}
              required={false}
            />
            {isExtern && (
              <>
                <Input
                  color="outline-grey"
                  name="phonenumber"
                  label="Telefoonnummer"
                  required={false}
                />
                <Input
                  color="outline-grey"
                  name="company"
                  label="Bedrijf"
                  required={false}
                />
                <Input
                  color="outline-grey"
                  name="website"
                  label="Website"
                  required={false}
                />
              </>
            )}
            <Input
              color="outline-grey"
              name="password"
              label={translate('general.terms.password')}
              required={false}
              autoComplete="new-password"
            />
            <Input
              color="outline-grey"
              name="password_confirmation"
              label={translate('general.terms.password-check')}
              required={false}
            />
            <FormDropdown
              name="language"
              label={translate('general.terms.language')}
              dropdown={
                <Dropdown
                  initialText={user?.language || formValues.language}
                  initialOptions={['nl', 'en']}
                  selectedItem={selectedItem}
                  setSelectedItem={setSelectedItem}
                  readOnly
                />
              }
              value={selectedItem}
            />
          </FormContext>
          <div styleName="button-wrapper">
            <Button>
              {translate('user.settings.save-details')}
              <Spinner loading={isSubmitting} />
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
